"use client";

import * as React from "react";
import {
  createBridgeRepository,
  type BridgeRepositoryDeps,
} from "./BridgeRepository";
import type {
  BridgeAmountSource,
  BridgeApprovalInfo,
  BridgeApproveFn,
  BridgeFailedInfo,
  BridgeGetApprovalFn,
  BridgeGetToTokensFn,
  BridgeHistoryItem,
  BridgeQuoteInput,
  BridgeStatus,
  BridgeStep,
  BridgeSubmittedInfo,
  BridgeToken,
  BridgeTxSummary,
} from "../types";
import { normalizeFailureReason, trackDappUiFunnel } from "../../../lib/sentry";

// Input limits for the amount field: max 15 integer digits, max 18 decimal digits.
const AMOUNT_MAX_INTEGER_DIGITS = 15;
const AMOUNT_MAX_DECIMAL_DIGITS = 18;
const TERMS_ACCEPTED_COOKIE_NAME = "cross_bridge_terms_accepted";
const TERMS_ACCEPTED_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function sanitizeAmountInput(value: string): string {
  let cleaned = value.replace(/[^\d.]/g, "");
  // Keep only the first decimal point.
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "");
  }
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex === -1) {
    return cleaned.slice(0, AMOUNT_MAX_INTEGER_DIGITS);
  }
  const intPart = cleaned.slice(0, dotIndex).slice(0, AMOUNT_MAX_INTEGER_DIGITS);
  const decPart = cleaned.slice(dotIndex + 1).slice(0, AMOUNT_MAX_DECIMAL_DIGITS);
  return `${intPart}.${decPart}`;
}

function hasAcceptedTermsCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .some((cookie) => cookie === `${TERMS_ACCEPTED_COOKIE_NAME}=true`);
}

function persistAcceptedTermsCookie(): void {
  if (typeof document === "undefined") return;
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = [
    `${TERMS_ACCEPTED_COOKIE_NAME}=true`,
    "Path=/",
    `Max-Age=${TERMS_ACCEPTED_COOKIE_MAX_AGE}`,
    "SameSite=Lax",
    secure,
  ].filter(Boolean).join("; ");
}

export interface UseBridgeFlowOptions extends BridgeRepositoryDeps {
  tokens: BridgeToken[];
  initialFromToken?: BridgeToken;
  initialToToken?: BridgeToken;
  getToTokens?: BridgeGetToTokensFn;
  getApproval?: BridgeGetApprovalFn;
  approveBridge?: BridgeApproveFn;
  history?: BridgeHistoryItem[];
  initialSlippage?: string;
  termsUrl?: string;
  onSubmitted?: (info: BridgeSubmittedInfo) => void;
  onSuccess?: (info: BridgeSubmittedInfo) => void;
  onFailed?: (info: BridgeFailedInfo) => void;
  onExit?: () => void;
}

// crossd's Auto-slippage formula (swap-bridge/constants + SwapBridgeCard):
//   impact < 0.1%      → 1%        (AUTO_SLIPPAGE_MIN)
//   0.1% ≤ impact < 3% → impact×3, floored to 2dp, but never below 1%
//   impact ≥ 3%        → 10%       (AUTO_SLIPPAGE_MAX)
// `priceImpact` is the raw percent string from the quote summary (e.g. "0.5").
function resolveAutoSlippage(priceImpact?: string): string {
  const impact = Number(priceImpact ?? "0");
  if (!Number.isFinite(impact) || impact < 0.1) return "1";
  const trunc2 = (n: number) => Math.floor(n * 100) / 100;
  if (impact < 3) {
    const calc = trunc2(impact * 3);
    return String(calc < 1 ? 1 : calc);
  }
  return "10";
}

// Normalize a native-family symbol, dropping the testnet "t" prefix
// (e.g. "tONEUSD" → "ONEUSD", "tONE" → "ONE").
function normalizeCrossSymbol(symbol: string): string {
  const s = symbol.toUpperCase();
  return s.startsWith("T") && s.slice(1).startsWith("ONE") ? s.slice(1) : s;
}

// The ONE↔ONEUSD pair swaps through a Uniswap v3 pool, which doesn't expose a
// user-selectable slippage — it's fixed to Auto 1%. Detected by symbol on the
// same chain (ONEUSD only lives on the CROSS chain), testnet prefix aside.
function isUniswapV3Pair(a?: BridgeToken, b?: BridgeToken): boolean {
  if (!a || !b || a.chainId !== b.chainId) return false;
  const symbols = new Set([
    normalizeCrossSymbol(a.symbol),
    normalizeCrossSymbol(b.symbol),
  ]);
  return symbols.has("ONE") && symbols.has("ONEUSD");
}

function sameToken(a?: BridgeToken, b?: BridgeToken): boolean {
  return (
    !!a &&
    !!b &&
    a.chainId === b.chainId &&
    a.address.toLowerCase() === b.address.toLowerCase()
  );
}

function firstToken(tokens: BridgeToken[]): BridgeToken | undefined {
  return tokens[0];
}

function selectDefaultToToken(
  tokens: BridgeToken[],
  from?: BridgeToken,
  explicit?: BridgeToken,
  getToTokens?: BridgeGetToTokensFn,
): BridgeToken | undefined {
  if (explicit && !sameToken(explicit, from)) return explicit;
  const available = from ? getToTokens?.(from, tokens) : undefined;
  if (available?.length) return available[0];
  if (!from) return tokens[1] ?? firstToken(tokens);
  const fromIndex = tokens.findIndex((token) => sameToken(token, from));
  const nextToken = fromIndex >= 0 ? tokens[fromIndex + 1] : undefined;
  if (nextToken && !sameToken(nextToken, from)) return nextToken;
  return (
    tokens.find((token) => !sameToken(token, from)) ??
    from
  );
}

function buildInput({
  fromToken,
  toToken,
  fromAmount,
  toAmount,
  lastChangedBy,
  slippage,
}: {
  fromToken: BridgeToken;
  toToken: BridgeToken;
  fromAmount: string;
  toAmount: string;
  lastChangedBy: BridgeAmountSource;
  slippage: string;
}): BridgeQuoteInput {
  return {
    fromToken,
    toToken,
    fromAmount,
    toAmount,
    lastChangedBy,
    slippage,
  };
}

// The user declined the request in their wallet (not a real failure).
function collectErrorFields(err: unknown, depth = 0): string[] {
  if (!err || depth > 5) return [];
  if (typeof err === "string") return [err];
  if (!(err instanceof Error) && typeof err !== "object") return [String(err)];

  const errorLike = err as {
    name?: unknown;
    message?: unknown;
    shortMessage?: unknown;
    details?: unknown;
    code?: unknown;
    cause?: unknown;
    data?: unknown;
    error?: unknown;
  };
  const fields = [
    err instanceof Error ? err.name : errorLike.name,
    err instanceof Error ? err.message : errorLike.message,
    errorLike.shortMessage,
    errorLike.details,
    errorLike.code,
  ]
    .filter((value): value is string | number => typeof value === "string" || typeof value === "number")
    .map(String);

  return [
    ...fields,
    ...collectErrorFields(errorLike.cause, depth + 1),
    ...collectErrorFields(errorLike.data, depth + 1),
    ...collectErrorFields(errorLike.error, depth + 1),
  ];
}

function isUserRejection(err: unknown): boolean {
  const haystack = collectErrorFields(err).join(" ").toLowerCase();
  const isCrossxTypedDataSignatureFailure =
    haystack.includes("signature_failed") &&
    (haystack.includes("타입 데이터 서명에 실패") ||
      haystack.includes("typed data") ||
      haystack.includes("signtypeddata"));
  return (
    haystack.includes("4001") ||
    haystack.includes("err_user_rejected") ||
    haystack.includes("userrejectedrequesterror") ||
    haystack.includes("user rejected") ||
    haystack.includes("user denied") ||
    haystack.includes("user cancelled") ||
    haystack.includes("user canceled") ||
    haystack.includes("rejected the request") ||
    haystack.includes("request rejected") ||
    haystack.includes("action_rejected") ||
    haystack.includes("rejected by user") ||
    isCrossxTypedDataSignatureFailure
  );
}

function bridgeErrorMessage(err: unknown, fallback: string): string {
  if (isUserRejection(err)) {
    return "Transaction request was rejected.";
  }
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : "";
  return raw.split("\n")[0]?.trim() || fallback;
}

export function useBridgeFlow(options: UseBridgeFlowOptions) {
  const {
    tokens,
    initialFromToken,
    initialToToken,
    getToTokens,
    getApproval,
    approveBridge,
    history = [],
    initialSlippage = "1",
    // 미설정 시 apps.json(legal.termsUrl) → DEFAULT_TERMS_URL 순으로 UI에서
    // 폴백한다. 여기서 기본값을 넣으면 "미설정"을 구분할 수 없어 그대로 둔다.
    termsUrl,
    getQuote,
    submitBridge,
    onSubmitted,
    onSuccess,
    onFailed,
    onExit,
  } = options;

  const repository = React.useMemo(
    () => createBridgeRepository({ getQuote, submitBridge }),
    [getQuote, submitBridge],
  );

  const [step, setStep] = React.useState<BridgeStep>("form");
  const [fromToken, setFromToken] = React.useState<BridgeToken | undefined>(
    initialFromToken ?? firstToken(tokens),
  );
  const [toToken, setToToken] = React.useState<BridgeToken | undefined>(() =>
    selectDefaultToToken(
      tokens,
      initialFromToken ?? firstToken(tokens),
      initialToToken,
      getToTokens,
    ),
  );
  const [fromAmount, setFromAmount] = React.useState("");
  const [toAmount, setToAmount] = React.useState("");
  const [lastChangedBy, setLastChangedBy] =
    React.useState<BridgeAmountSource>("from");
  const [slippage, setSlippage] = React.useState(initialSlippage);
  const [summary, setSummary] = React.useState<BridgeTxSummary | null>(null);
  // The concrete percent used for amountOutMinimum at submit. "auto" resolves
  // via crossd's price-impact formula; an explicit preset/custom passes through.
  // Note: this only affects the submitted tx (slippage doesn't change the quote
  // amounts or the displayed gas estimate), so feeding it back into the quote
  // would be pointless — the quote keeps using the raw `slippage` mode.
  // Uniswap v3 pairs (ONE↔ONEUSD) ignore the chosen slippage and lock to 1%.
  const slippageLocked = React.useMemo(
    () => isUniswapV3Pair(fromToken, toToken),
    [fromToken, toToken],
  );
  const effectiveSlippage = React.useMemo(
    () =>
      slippageLocked
        ? "1"
        : slippage === "auto"
          ? resolveAutoSlippage(summary?.priceImpact)
          : slippage,
    [slippageLocked, slippage, summary?.priceImpact],
  );
  const [quoteError, setQuoteError] = React.useState("");
  const [status, setStatus] = React.useState<BridgeStatus>("idle");
  const [error, setError] = React.useState("");
  const [submitted, setSubmitted] = React.useState<BridgeSubmittedInfo | null>(
    null,
  );
  const [localHistory, setLocalHistory] = React.useState<BridgeHistoryItem[]>([]);
  const [termsOpen, setTermsOpen] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(
    hasAcceptedTermsCookie,
  );
  const [approvalInfo, setApprovalInfo] =
    React.useState<BridgeApprovalInfo | null>(null);
  const [approvalSuccessOpen, setApprovalSuccessOpen] = React.useState(false);
  const [pendingSubmitInput, setPendingSubmitInput] =
    React.useState<BridgeQuoteInput | null>(null);
  const [submitToast, setSubmitToast] =
    React.useState<"processing" | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = React.useState(false);
  const [hasNewHistory, setHasNewHistory] = React.useState(false);
  const quoteReqIdRef = React.useRef(0);
  const toastTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitLockRef = React.useRef(false);
  const approvalLockRef = React.useRef(false);
  const funnelOpenedRef = React.useRef(false);
  const funnelTokenSelectedRef = React.useRef(false);
  const funnelAmountEnteredRef = React.useRef(false);
  const funnelSubmittedRef = React.useRef(false);
  const funnelApprovedRef = React.useRef(false);

  React.useEffect(() => {
    if (funnelOpenedRef.current) return;
    funnelOpenedRef.current = true;
    trackDappUiFunnel("bridge", "opened");
  }, []);

  const clearToastTimer = React.useCallback(() => {
    if (!toastTimerRef.current) return;
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = null;
  }, []);

  const showSubmitToast = React.useCallback(
    (next: "processing" | null) => {
      clearToastTimer();
      setSubmitToast(next);
    },
    [clearToastTimer],
  );

  React.useEffect(() => clearToastTimer, [clearToastTimer]);

  React.useEffect(() => {
    const nextFrom =
      tokens.find((token) => sameToken(token, fromToken)) ?? firstToken(tokens);
    const available = nextFrom ? getToTokens?.(nextFrom, tokens) : undefined;
    const nextTo =
      available?.find((token) => sameToken(token, toToken)) ??
      (!available ? tokens.find((token) => sameToken(token, toToken)) : undefined) ??
      selectDefaultToToken(tokens, nextFrom, undefined, getToTokens);
    setFromToken(nextFrom);
    setToToken(nextTo);
  }, [getToTokens, tokens, fromToken, toToken]);

  const setAmount = React.useCallback(
    (value: string, source: BridgeAmountSource) => {
      const sanitized = sanitizeAmountInput(value);
      if (!sanitized) {
        quoteReqIdRef.current += 1;
        setFromAmount("");
        setToAmount("");
        setLastChangedBy(source);
        setSummary(null);
        setQuoteError("");
        setStatus("idle");
        return;
      }
      if (source === "from") {
        setFromAmount(sanitized);
      } else {
        setToAmount(sanitized);
      }
      setLastChangedBy(source);
      setQuoteError("");
      if (!funnelAmountEnteredRef.current && Number(sanitized) > 0) {
        funnelAmountEnteredRef.current = true;
        trackDappUiFunnel("bridge", "amount_entered");
      }
    },
    [],
  );

  const resetQuoteState = React.useCallback(() => {
    quoteReqIdRef.current += 1;
    setFromAmount("");
    setToAmount("");
    setLastChangedBy("from");
    setSummary(null);
    setQuoteError("");
    setError("");
    setStatus("idle");
    setApprovalInfo(null);
    setApprovalSuccessOpen(false);
    setPendingSubmitInput(null);
    setSuccessDialogOpen(false);
    showSubmitToast(null);
  }, [showSubmitToast]);

  const selectFromToken = React.useCallback(
    (token: BridgeToken) => {
      if (sameToken(token, fromToken)) return;
      if (!funnelTokenSelectedRef.current) {
        funnelTokenSelectedRef.current = true;
        trackDappUiFunnel("bridge", "token_selected", {
          tags: { item_id: `from:${token.symbol}` },
        });
      }
      setFromToken(token);
      setToToken(selectDefaultToToken(tokens, token, undefined, getToTokens));
      resetQuoteState();
    },
    [fromToken, getToTokens, resetQuoteState, tokens],
  );

  const selectToToken = React.useCallback(
    (token: BridgeToken) => {
      if (sameToken(token, toToken) || sameToken(token, fromToken)) return;
      if (!funnelTokenSelectedRef.current) {
        funnelTokenSelectedRef.current = true;
        trackDappUiFunnel("bridge", "token_selected", {
          tags: { item_id: `to:${token.symbol}` },
        });
      }
      setToToken(token);
      resetQuoteState();
    },
    [fromToken, resetQuoteState, toToken],
  );

  const availableToTokens = React.useMemo(() => {
    if (!fromToken) return [];
    return getToTokens?.(fromToken, tokens) ?? tokens.filter((token) => !sameToken(token, fromToken));
  }, [fromToken, getToTokens, tokens]);

  const setMax = React.useCallback(() => {
    if (!fromToken?.balance) return;
    setAmount(fromToken.balance, "from");
  }, [fromToken?.balance, setAmount]);

  const swapTokens = React.useCallback(() => {
    if (!fromToken || !toToken) return;
    setFromToken(toToken);
    setToToken(fromToken);
    resetQuoteState();
  }, [fromToken, resetQuoteState, toToken]);

  const resetInputAfterSubmit = React.useCallback(() => {
    quoteReqIdRef.current += 1;
    setFromAmount("");
    setToAmount("");
    setLastChangedBy("from");
    setSummary(null);
    setQuoteError("");
    setError("");
  }, []);

  React.useEffect(() => {
    if (!fromToken || !toToken) return;
    if (!fromAmount && !toAmount) {
      setSummary(null);
      setQuoteError("");
      setStatus((current) => (current === "quoting" ? "idle" : current));
      return;
    }

    const reqId = ++quoteReqIdRef.current;
    const input = buildInput({
      fromToken,
      toToken,
      fromAmount,
      toAmount,
      lastChangedBy,
      slippage,
    });
    setStatus((current) =>
      current === "submitting"
        ? current
        : repository.canQuote
          ? "quoting"
          : "idle",
    );
    void (async () => {
      try {
        const result = await repository.getQuote(input);
        if (quoteReqIdRef.current !== reqId) return;
        if (result.fromAmount !== undefined && lastChangedBy === "to") {
          setFromAmount(result.fromAmount);
        }
        if (result.toAmount !== undefined && lastChangedBy === "from") {
          setToAmount(result.toAmount);
        }
        setSummary(result.summary ?? null);
        setQuoteError(result.error ?? "");
      } catch (err) {
        if (quoteReqIdRef.current !== reqId) return;
        setQuoteError(err instanceof Error ? err.message : "Failed to quote bridge.");
      } finally {
        if (quoteReqIdRef.current === reqId) {
          setStatus((current) => (current === "quoting" ? "idle" : current));
        }
      }
    })();
  }, [
    fromAmount,
    fromToken,
    lastChangedBy,
    repository,
    slippage,
    toAmount,
    toToken,
  ]);

  const getCurrentInput = React.useCallback(() => {
    if (!fromToken || !toToken) return null;
    return buildInput({
      fromToken,
      toToken,
      fromAmount,
      toAmount,
      lastChangedBy,
      slippage: effectiveSlippage,
    });
  }, [effectiveSlippage, fromAmount, fromToken, lastChangedBy, toAmount, toToken]);

  const runSubmit = React.useCallback(async (
    input: BridgeQuoteInput,
    options?: { skipApprovalCheck?: boolean },
  ) => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setError("");
    setSuccessDialogOpen(false);
    setApprovalSuccessOpen(false);
    setStatus("submitting");
    try {
      if (!options?.skipApprovalCheck && getApproval) {
        const nextApproval = await getApproval(input);
        if (nextApproval) {
          setApprovalInfo(nextApproval);
          setPendingSubmitInput(input);
          setStatus("idle");
          return;
        }
      }
      setApprovalInfo(null);
      setPendingSubmitInput(null);
      showSubmitToast("processing");
      const info = await repository.submitBridge(input);
      setSubmitted(info);
      setStatus("success");
      setLocalHistory((items) => [
        {
          id: info.txHash,
          timestamp: new Date().toISOString(),
          txHash: info.txHash,
          fromToken: info.fromToken,
          toToken: info.toToken,
          fromAmount: info.fromAmount,
          toAmount: info.toAmount,
          summary: summary ?? undefined,
          status: "pending",
        },
        ...items.filter((item) => item.txHash !== info.txHash),
      ]);
      resetInputAfterSubmit();
      setHasNewHistory(true);
      showSubmitToast(null);
      setSuccessDialogOpen(true);
      trackDappUiFunnel("bridge", "confirmed");
      funnelSubmittedRef.current = false;
      funnelApprovedRef.current = false;
      onSubmitted?.(info);
      onSuccess?.(info);
    } catch (err) {
      showSubmitToast(null);
      trackDappUiFunnel("bridge", "confirmed", {
        status: "failure",
        reason: normalizeFailureReason(err),
      });
      funnelSubmittedRef.current = false;
      funnelApprovedRef.current = false;
      // A wallet cancellation isn't an error — return to idle silently.
      if (isUserRejection(err)) {
        setStatus("idle");
        return;
      }
      const message = bridgeErrorMessage(err, "Failed to submit bridge.");
      setError(message);
      setStatus("error");
      onFailed?.({ message });
    } finally {
      submitLockRef.current = false;
    }
  }, [
    getApproval,
    onFailed,
    onSubmitted,
    onSuccess,
    repository,
    resetInputAfterSubmit,
    showSubmitToast,
    summary,
  ]);

  const submit = React.useCallback(async () => {
    if (quoteError) return;
    const input = getCurrentInput();
    if (!input) return;
    if (!funnelSubmittedRef.current) {
      funnelSubmittedRef.current = true;
      trackDappUiFunnel("bridge", "submitted");
    }
    if (!termsAccepted) {
      setPendingSubmitInput(input);
      setTermsOpen(true);
      return;
    }
    await runSubmit(input);
  }, [getCurrentInput, quoteError, runSubmit, termsAccepted]);

  const confirmTerms = React.useCallback(async () => {
    const input = pendingSubmitInput ?? getCurrentInput();
    if (!input) return;
    persistAcceptedTermsCookie();
    setTermsAccepted(true);
    setTermsOpen(false);
    await runSubmit(input);
  }, [getCurrentInput, pendingSubmitInput, runSubmit]);

  const cancelTerms = React.useCallback(() => {
    setTermsOpen(false);
    setPendingSubmitInput(null);
  }, []);

  const confirmApproval = React.useCallback(async () => {
    if (
      approvalLockRef.current ||
      status === "submitting" ||
      !approvalInfo ||
      !pendingSubmitInput ||
      !approveBridge
    ) {
      return;
    }
    approvalLockRef.current = true;
    setError("");
    setStatus("submitting");
    try {
      await approveBridge(pendingSubmitInput, approvalInfo);
      if (!funnelApprovedRef.current) {
        funnelApprovedRef.current = true;
        trackDappUiFunnel("bridge", "approved");
      }
      setApprovalInfo(null);
      setStatus("idle");
      setApprovalSuccessOpen(true);
    } catch (err) {
      // A wallet cancellation isn't an error — return to idle silently.
      if (isUserRejection(err)) {
        setStatus("idle");
        return;
      }
      const message = bridgeErrorMessage(err, "Failed to approve token.");
      setError(message);
      setStatus("error");
      onFailed?.({ message });
    } finally {
      approvalLockRef.current = false;
    }
  }, [
    approvalInfo,
    approveBridge,
    onFailed,
    pendingSubmitInput,
    runSubmit,
    status,
  ]);

  const cancelApproval = React.useCallback(() => {
    if (status === "submitting") return;
    setApprovalInfo(null);
    setApprovalSuccessOpen(false);
    setPendingSubmitInput(null);
    setStatus("idle");
  }, [status]);

  const continueAfterApproval = React.useCallback(async () => {
    if (status === "submitting") return;
    const input = pendingSubmitInput ?? getCurrentInput();
    if (!input) {
      setApprovalSuccessOpen(false);
      setError("Bridge input is no longer available. Please try again.");
      setStatus("error");
      return;
    }
    setApprovalSuccessOpen(false);
    await runSubmit(input, { skipApprovalCheck: true });
  }, [getCurrentInput, pendingSubmitInput, runSubmit, status]);

  const dismissSuccessDialog = React.useCallback(() => {
    setSuccessDialogOpen(false);
  }, []);

  const setBridgeStep = React.useCallback((next: BridgeStep) => {
    setStep(next);
    if (next === "history") {
      setHasNewHistory(false);
    }
  }, []);

  const back = React.useCallback(() => {
    if (step === "history") {
      setBridgeStep("form");
      return;
    }
    onExit?.();
  }, [onExit, setBridgeStep, step]);

  const mergedHistory = React.useMemo(
    () => [
      ...history,
      ...localHistory.filter(
        (item) =>
          !history.some((remoteItem) => remoteItem.txHash === item.txHash),
      ),
    ],
    [history, localHistory],
  );

  return {
    step,
    setStep: setBridgeStep,
    status,
    error,
    quoteError,
    fromToken,
    toToken,
    tokens,
    availableToTokens,
    fromAmount,
    toAmount,
    lastChangedBy,
    slippage,
    effectiveSlippage,
    slippageLocked,
    setSlippage,
    summary,
    history: mergedHistory,
    submitted,
    canSubmit: repository.canSubmit,
    termsOpen,
    approvalInfo,
    approvalSuccessOpen,
    approveBridge,
    pendingSubmitInput,
    submitToast,
    successDialogOpen,
    dismissSuccessDialog,
    hasNewHistory,
    termsUrl,
    setFromToken,
    setToToken,
    selectFromToken,
    selectToToken,
    setAmount,
    setMax,
    swapTokens,
    submit,
    confirmTerms,
    cancelTerms,
    confirmApproval,
    cancelApproval,
    continueAfterApproval,
    back,
  };
}

export type BridgeFlow = ReturnType<typeof useBridgeFlow>;
